// src/components/ManutencaoRelatorioModal.jsx
//
// Modal de relatório detalhado de uma manutenção vinculada ao veículo.
// Abre ao clicar em um card da aba "Manutenções" no VeiculoEditModal.
// Busca automaticamente os Campos Extras (CamposExtrasTab) para exibir tudo.
//
import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import {
    FaTimes, FaSpinner, FaWrench, FaBuilding, FaCalendarAlt,
    FaDollarSign, FaListAlt, FaTachometerAlt, FaShieldAlt,
    FaUserCog, FaStickyNote, FaChartLine, FaExclamationTriangle,
    FaCheckCircle, FaPauseCircle, FaStopCircle, FaSearch,
    FaDownload, FaPrint, FaFileInvoiceDollar, FaClock,
} from 'react-icons/fa';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmt = (v) => (v === null || v === undefined || v === '') ? null : String(v);

const fmtDate = (v) => {
    if (!v) return null;
    const d = new Date(v);
    return isNaN(d) ? String(v) : d.toLocaleDateString('pt-BR', { timeZone: 'UTC', day: '2-digit', month: '2-digit', year: 'numeric' });
};

const fmtMoney = (v) => {
    const n = parseFloat(v);
    if (!Number.isFinite(n)) return null;
    return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const fmtNum = (v, suffix = '') => {
    const n = parseFloat(v);
    if (!Number.isFinite(n) || n <= 0) return null;
    return `${n.toLocaleString('pt-BR')}${suffix}`;
};

// ─── Badge de status ─────────────────────────────────────────────────────────
const STATUS_MAP = {
    concluido:            { color: '#166534', bg: '#dcfce7', border: '#86efac', icon: <FaCheckCircle />,  label: 'Concluído'           },
    aprovado:             { color: '#1e40af', bg: '#dbeafe', border: '#93c5fd', icon: <FaCheckCircle />,  label: 'Aprovado / Em Exec.' },
    aguardando_orcamento: { color: '#b45309', bg: '#fef3c7', border: '#fde68a', icon: <FaClock />,        label: 'Aguardando Orçamento'},
    em_analise:           { color: '#6b21a8', bg: '#f3e8ff', border: '#d8b4fe', icon: <FaSearch />,       label: 'Em Análise'          },
    pendente:             { color: '#92400e', bg: '#fef3c7', border: '#fde68a', icon: <FaPauseCircle />,  label: 'Pendente'            },
    recusado:             { color: '#991b1b', bg: '#fee2e2', border: '#fca5a5', icon: <FaStopCircle />,   label: 'Reprovado'           },
};

function StatusBadge({ status }) {
    const s = STATUS_MAP[status] || { color: '#475569', bg: '#f1f5f9', border: '#e2e8f0', icon: <FaWrench />, label: status || '—' };
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '5px 14px', borderRadius: 20,
            background: s.bg, color: s.color, border: `1px solid ${s.border}`,
            fontSize: '0.82rem', fontWeight: 700,
        }}>
            {s.icon} {s.label}
        </span>
    );
}

// ─── Seção do relatório ───────────────────────────────────────────────────────
function Section({ color = '#2563eb', icon, title, children }) {
    return (
        <div style={{
            marginBottom: 16, border: '1.5px solid #e2e8f0',
            borderLeft: `4px solid ${color}`, borderRadius: 10, overflow: 'hidden',
        }}>
            <div style={{
                display: 'flex', alignItems: 'center', gap: 9,
                padding: '9px 14px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0',
            }}>
                <span style={{ color, fontSize: '0.85rem' }}>{icon}</span>
                <span style={{ fontSize: '0.73rem', fontWeight: 800, color, textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                    {title}
                </span>
            </div>
            <div style={{ padding: '12px 14px' }}>
                {children}
            </div>
        </div>
    );
}

// Grid de campos label + valor
function Grid({ cols = 3, items }) {
    const filled = items.filter(i => i.value !== null && i.value !== undefined && i.value !== '');
    if (!filled.length) return <p style={{ color: '#94a3b8', fontSize: '0.82rem', margin: 0 }}>Nenhuma informação preenchida.</p>;
    return (
        <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${cols}, 1fr)`,
            gap: 10,
        }}>
            {filled.map(({ label, value, span }, i) => (
                <div key={i} style={{
                    background: '#f8fafc', border: '1px solid #e2e8f0',
                    borderRadius: 8, padding: '10px 12px',
                    gridColumn: span ? `span ${span}` : undefined,
                }}>
                    <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 3 }}>
                        {label}
                    </div>
                    <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#1e293b' }}>
                        {value}
                    </div>
                </div>
            ))}
        </div>
    );
}

// ─── PRIORIDADE BADGE ─────────────────────────────────────────────────────────
const PRIO = {
    urgente: { emoji: '🔴', label: 'Urgente' },
    alta:    { emoji: '🟠', label: 'Alta'    },
    media:   { emoji: '🟡', label: 'Média'   },
    baixa:   { emoji: '🟢', label: 'Baixa'   },
};

// ─── COMPONENTE PRINCIPAL ─────────────────────────────────────────────────────
export default function ManutencaoRelatorioModal({ manutencao, veiculo, onClose }) {
    const [extras,        setExtras]        = useState(null);
    const [loadingExtras, setLoadingExtras] = useState(true);

    const fetchExtras = useCallback(async () => {
        if (!manutencao?.id) return;
        setLoadingExtras(true);
        try {
            const res = await api.get(`/api/frota/manutencoes/${manutencao.id}/campos-extras`);
            setExtras(res.data || {});
        } catch {
            setExtras({});   // 404 = não preenchido ainda
        } finally {
            setLoadingExtras(false);
        }
    }, [manutencao?.id]);

    useEffect(() => { fetchExtras(); }, [fetchExtras]);

    if (!manutencao) return null;

    const e = extras || {};   // atalho

    // ── Impressão ──────────────────────────────────────────────────────────────
    const handlePrint = () => {
        const conteudo = document.getElementById('mr-printable');
        if (!conteudo) return;
        const html = `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8">
            <title>Relatório Manutenção #${manutencao.id}</title>
            <style>
                * { box-sizing:border-box; margin:0; padding:0; }
                body { font-family:'Segoe UI',Arial,sans-serif; color:#1e293b; font-size:12px; padding:20px; }
                h1 { font-size:18px; margin-bottom:4px; }
                .sub { color:#64748b; font-size:11px; margin-bottom:20px; }
                .sec { margin-bottom:14px; border:1px solid #e2e8f0; border-left:3px solid #2563eb; border-radius:6px; overflow:hidden; }
                .sec-h { background:#f8fafc; padding:7px 12px; font-size:10px; font-weight:800; text-transform:uppercase; letter-spacing:.6px; color:#64748b; border-bottom:1px solid #e2e8f0; }
                .sec-b { padding:10px 12px; }
                .grid { display:grid; grid-template-columns:repeat(3,1fr); gap:8px; }
                .cell { background:#f8fafc; border:1px solid #e2e8f0; border-radius:6px; padding:8px 10px; }
                .cell label { display:block; font-size:9px; font-weight:700; color:#94a3b8; text-transform:uppercase; margin-bottom:2px; }
                .cell span { font-size:11px; font-weight:600; color:#1e293b; }
                @media print { @page { margin:15mm; } }
            </style></head><body>
            ${conteudo.innerHTML}
            </body></html>`;
        const w = window.open('', '_blank');
        if (w) { w.document.write(html); w.document.close(); w.print(); }
    };

    return (
        <div
            style={{
                position: 'fixed', inset: 0, zIndex: 10100,
                background: 'rgba(15,23,42,0.65)', backdropFilter: 'blur(6px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 16,
            }}
            onClick={onClose}
        >
            <div
                style={{
                    background: '#fff', borderRadius: 16, width: '100%', maxWidth: 820,
                    maxHeight: '90vh', display: 'flex', flexDirection: 'column',
                    boxShadow: '0 32px 80px rgba(0,0,0,.35)',
                    overflow: 'hidden',
                }}
                onClick={e => e.stopPropagation()}
            >
                {/* ── Cabeçalho do modal ── */}
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '16px 22px', background: '#0f172a', color: '#fff', flexShrink: 0,
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <div style={{
                            width: 42, height: 42, borderRadius: 10,
                            background: 'rgba(255,255,255,.12)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '1.15rem', color: '#7dd3fc',
                        }}>
                            <FaWrench />
                        </div>
                        <div>
                            <div style={{ fontWeight: 800, fontSize: '1.05rem' }}>
                                Relatório — Manutenção #{manutencao.id}
                            </div>
                            <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,.55)', marginTop: 2 }}>
                                {veiculo?.placa} · {veiculo?.marca} {veiculo?.modelo}
                                {veiculo?.ano ? ` · ${veiculo.ano}` : ''}
                            </div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', align: 'center', gap: 8 }}>
                        <button
                            onClick={handlePrint}
                            title="Imprimir / salvar PDF"
                            style={{
                                background: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.2)',
                                borderRadius: 8, color: '#e2e8f0', padding: '7px 14px', cursor: 'pointer',
                                fontSize: '0.82rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6,
                                fontFamily: 'inherit',
                            }}
                        >
                            <FaPrint /> Imprimir
                        </button>
                        <button
                            onClick={onClose}
                            style={{
                                background: 'none', border: 'none', color: 'rgba(255,255,255,.6)',
                                cursor: 'pointer', fontSize: '1.3rem', display: 'flex', alignItems: 'center',
                                padding: '4px 6px', borderRadius: 6,
                            }}
                        >
                            <FaTimes />
                        </button>
                    </div>
                </div>

                {/* ── Corpo scrollável ── */}
                <div id="mr-printable" style={{ overflowY: 'auto', padding: '20px 22px', flex: 1 }}>

                    {/* Status + Oficina rápida */}
                    <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        flexWrap: 'wrap', gap: 10, marginBottom: 18,
                        padding: '12px 16px', background: '#f8fafc',
                        border: '1.5px solid #e2e8f0', borderRadius: 10,
                    }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <StatusBadge status={manutencao.status} />
                            {manutencao.oficina_nome && manutencao.oficina_nome !== 'Não informada' && (
                                <span style={{ fontSize: '0.8rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: 5, marginTop: 4 }}>
                                    <FaBuilding style={{ color: '#94a3b8' }} /> {manutencao.oficina_nome}
                                </span>
                            )}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                            {fmtMoney(manutencao.valor_final) && (
                                <span style={{ fontSize: '1.25rem', fontWeight: 800, color: '#166534' }}>
                                    {fmtMoney(manutencao.valor_final)}
                                </span>
                            )}
                            {manutencao.arquivo_nf_url && (
                                <a
                                    href={`${api.defaults.baseURL}/${String(manutencao.arquivo_nf_url).replace(/\\/g, '/')}`}
                                    target="_blank" rel="noopener noreferrer"
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 5,
                                        fontSize: '0.75rem', color: '#2563eb', fontWeight: 600,
                                        textDecoration: 'none',
                                    }}
                                >
                                    <FaDownload /> Ver Nota Fiscal
                                </a>
                            )}
                        </div>
                    </div>

                    {/* ── 1. SERVIÇO ── */}
                    <Section color="#0f172a" icon={<FaWrench />} title="Serviço">
                        <Grid cols={3} items={[
                            { label: 'Tipo de Serviço',    value: fmt(manutencao.tipo_servico) },
                            { label: 'Data de Abertura',   value: fmtDate(manutencao.data_abertura) },
                            { label: 'Data de Conclusão',  value: fmtDate(manutencao.data_conclusao) },
                            { label: 'Descrição / Problema', value: fmt(manutencao.descricao_problema), span: 3 },
                        ]} />
                    </Section>

                    {/* ── Loading extras ── */}
                    {loadingExtras ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32, color: '#94a3b8', gap: 10 }}>
                            <FaSpinner style={{ animation: 'mr-spin 0.8s linear infinite' }} /> Carregando campos extras…
                        </div>
                    ) : (
                        <>
                            {/* ── 2. IDENTIFICAÇÃO (Campos Extras) ── */}
                            <Section color="#2563eb" icon={<FaBuilding />} title="Identificação">
                                <Grid cols={3} items={[
                                    { label: 'Unidade Organizacional',   value: fmt(e.unidade_organizacional) },
                                    { label: 'Grupo da Unidade Rastreada', value: fmt(e.grupo_unidade_rastreada) },
                                    { label: 'Tipo da Manutenção',       value: fmt(e.tipo_manutencao) },
                                    { label: 'Nome da Etapa',            value: fmt(e.nome_etapa) },
                                    { label: 'Situação',                  value: fmt(e.situacao) },
                                    { label: 'Prioridade',                value: e.prioridade ? `${PRIO[e.prioridade]?.emoji || ''} ${PRIO[e.prioridade]?.label || e.prioridade}` : null },
                                ]} />
                            </Section>

                            {/* ── 3. DATAS ── */}
                            <Section color="#7c3aed" icon={<FaCalendarAlt />} title="Datas">
                                <Grid cols={4} items={[
                                    { label: 'Data Planejada',           value: fmtDate(e.data_planejada) },
                                    { label: 'Data de Realização',       value: fmtDate(e.data_realizacao) },
                                    { label: 'Próxima Revisão (Data)',   value: fmtDate(e.proxima_revisao_data) },
                                    { label: 'Vigência da Garantia',     value: fmtDate(e.vigencia_garantia) },
                                ]} />
                            </Section>

                            {/* ── 4. MÉTRICAS ── */}
                            <Section color="#0891b2" icon={<FaTachometerAlt />} title="Métricas do Veículo">
                                <Grid cols={4} items={[
                                    { label: 'Km Planejado',           value: fmtNum(e.km_planejado, ' km') },
                                    { label: 'Km Real',                value: fmtNum(e.km_real, ' km') },
                                    { label: 'Horímetro Planejado',    value: fmtNum(e.horimetro_planejado, ' h') },
                                    { label: 'Horímetro Real',         value: fmtNum(e.horimetro_real, ' h') },
                                    { label: 'Hodômetro Entrada',      value: fmtNum(e.hodometro_entrada, ' km') },
                                    { label: 'Hodômetro Saída',        value: fmtNum(e.hodometro_saida, ' km') },
                                    { label: 'Nível Combustível',      value: fmtNum(e.nivel_combustivel, '%') },
                                    { label: 'Tempo da Manutenção',    value: fmtNum(e.tempo_manutencao, ' h') },
                                    { label: 'Próxima Revisão (Km)',   value: fmtNum(e.proxima_revisao_km, ' km') },
                                ]} />
                            </Section>

                            {/* ── 5. DIAGNÓSTICO ── */}
                            <Section color="#dc2626" icon={<FaWrench />} title="Diagnóstico de Falha">
                                <Grid cols={3} items={[
                                    { label: 'Tipo de Falha',       value: fmt(e.tipo_falha) },
                                    { label: 'Modo de Falha',       value: fmt(e.modo_falha) },
                                    { label: 'Impacto Operacional', value: fmt(e.impacto_operacional) },
                                    { label: 'Código de Falha (DTC)', value: fmt(e.codigo_falha) },
                                ]} />
                            </Section>

                            {/* ── 6. ITENS ── */}
                            <Section color="#d97706" icon={<FaListAlt />} title="Itens da Manutenção">
                                <Grid cols={5} items={[
                                    { label: 'Previstos',     value: fmt(e.itens_previstos) },
                                    { label: 'Realizados',    value: fmt(e.itens_realizados) },
                                    { label: 'Não Previstos', value: fmt(e.itens_nao_previstos) },
                                    { label: 'Cancelados',    value: fmt(e.itens_cancelados) },
                                    { label: 'Migrados',      value: fmt(e.itens_migrados) },
                                ]} />
                            </Section>

                            {/* ── 7. CUSTOS PLANEJADOS ── */}
                            <Section color="#16a34a" icon={<FaDollarSign />} title="Custos Planejados">
                                <Grid cols={3} items={[
                                    { label: 'Peças Planejadas',     value: fmtMoney(e.custo_pecas_planejadas) },
                                    { label: 'Serviços Planejados',  value: fmtMoney(e.custo_servicos_planejados) },
                                    { label: 'Total Planejado',      value: fmtMoney(e.custo_total_planejado) },
                                ]} />
                            </Section>

                            {/* ── 8. CUSTOS REALIZADOS ── */}
                            <Section color="#ea580c" icon={<FaChartLine />} title="Custos Realizados">
                                <Grid cols={3} items={[
                                    { label: 'Peças Realizadas',                value: fmtMoney(e.custo_pecas_realizadas) },
                                    { label: 'Serviços Realizados',             value: fmtMoney(e.custo_servicos_realizados) },
                                    { label: 'Total Realizado',                 value: fmtMoney(e.custo_total_realizado) },
                                    { label: 'Realiz. Peças Planejadas',        value: fmtMoney(e.custo_realizacao_pecas_planejadas) },
                                    { label: 'Realiz. Serviços Planejados',     value: fmtMoney(e.custo_realizacao_servicos_planejados) },
                                    { label: 'Realiz. Peças NÃO Planejadas',    value: fmtMoney(e.custo_realizacao_pecas_nao_planejadas) },
                                    { label: 'Realiz. Serviços NÃO Planejados', value: fmtMoney(e.custo_realizacao_servicos_nao_planejados) },
                                    { label: 'Custo Aprovado (OS)',             value: fmtMoney(manutencao.valor_final) },
                                ]} />
                            </Section>

                            {/* ── 9. RESPONSÁVEIS & DOCUMENTOS ── */}
                            <Section color="#7c3aed" icon={<FaUserCog />} title="Responsáveis & Documentos">
                                <Grid cols={3} items={[
                                    { label: 'Responsável Técnico',      value: fmt(e.responsavel_tecnico) },
                                    { label: 'Aprovador Financeiro',     value: fmt(e.aprovador_financeiro) },
                                    { label: 'Centro de Custo',          value: fmt(e.centro_custo) },
                                    { label: 'Nº OS da Oficina (ext.)',  value: fmt(e.numero_os_oficina) },
                                    { label: 'Nº NF Peças',              value: fmt(e.numero_nf_pecas) },
                                    { label: 'Nº NF Serviços',           value: fmt(e.numero_nf_servicos) },
                                ]} />
                            </Section>

                            {/* ── 10. GARANTIA ── */}
                            {(e.garantia_aplicavel || e.vigencia_garantia) && (
                                <Section color="#0891b2" icon={<FaShieldAlt />} title="Garantia">
                                    <Grid cols={3} items={[
                                        { label: 'Garantia Aplicável', value: e.garantia_aplicavel === 'sim' ? '✅ Sim' : e.garantia_aplicavel === 'nao' ? '❌ Não' : e.garantia_aplicavel === 'parcial' ? '⚠️ Parcial' : null },
                                        { label: 'Vigência',           value: fmtDate(e.vigencia_garantia) },
                                    ]} />
                                </Section>
                            )}

                            {/* ── 11. PARECER & OBSERVAÇÕES ── */}
                            {(e.parecer_tecnico || e.observacao_manutencao) && (
                                <Section color="#475569" icon={<FaStickyNote />} title="Parecer & Observações">
                                    {e.parecer_tecnico && (
                                        <div style={{ marginBottom: e.observacao_manutencao ? 12 : 0 }}>
                                            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6 }}>
                                                Parecer Técnico
                                            </div>
                                            <div style={{
                                                background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
                                                padding: '10px 12px', fontSize: '0.875rem', color: '#334155',
                                                lineHeight: 1.6, whiteSpace: 'pre-wrap',
                                            }}>
                                                {e.parecer_tecnico}
                                            </div>
                                        </div>
                                    )}
                                    {e.observacao_manutencao && (
                                        <div>
                                            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6 }}>
                                                Observação
                                            </div>
                                            <div style={{
                                                background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8,
                                                padding: '10px 12px', fontSize: '0.875rem', color: '#78350f',
                                                lineHeight: 1.6, whiteSpace: 'pre-wrap',
                                            }}>
                                                {e.observacao_manutencao}
                                            </div>
                                        </div>
                                    )}
                                </Section>
                            )}

                            {/* Mensagem quando extras estão todos vazios */}
                            {Object.values(e).every(v => v === null || v === '' || v === undefined) && (
                                <div style={{
                                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                                    padding: '24px 16px', gap: 8,
                                    background: '#fafafa', border: '1.5px dashed #e2e8f0', borderRadius: 10,
                                }}>
                                    <FaExclamationTriangle style={{ color: '#d97706', fontSize: '1.4rem' }} />
                                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b', textAlign: 'center' }}>
                                        Campos extras não preenchidos para esta manutenção.<br />
                                        <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                                            Abra a OS em <strong>Gestão de Oficinas → Gerenciar → Campos Extras</strong> e preencha.
                                        </span>
                                    </p>
                                </div>
                            )}
                        </>
                    )}

                    {/* Rodapé do relatório */}
                    <div style={{
                        marginTop: 20, paddingTop: 12, borderTop: '1px solid #e2e8f0',
                        display: 'flex', justifyContent: 'space-between',
                        fontSize: '0.7rem', color: '#94a3b8',
                    }}>
                        <span>BLB System — Gestão de Frota</span>
                        <span>Relatório gerado em {new Date().toLocaleString('pt-BR')}</span>
                    </div>
                </div>

                {/* ── Footer do modal ── */}
                <div style={{
                    padding: '12px 22px', background: '#f8fafc', borderTop: '1px solid #e2e8f0',
                    display: 'flex', justifyContent: 'flex-end', gap: 10, flexShrink: 0,
                }}>
                    <button
                        onClick={handlePrint}
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: 7,
                            padding: '9px 18px', borderRadius: 9,
                            background: '#0f172a', color: '#fff', border: 'none',
                            fontSize: '0.88rem', fontWeight: 700, cursor: 'pointer',
                            fontFamily: 'inherit',
                        }}
                    >
                        <FaPrint /> Imprimir / PDF
                    </button>
                    <button
                        onClick={onClose}
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: 7,
                            padding: '9px 18px', borderRadius: 9,
                            background: 'transparent', color: '#64748b',
                            border: '1.5px solid #e2e8f0',
                            fontSize: '0.88rem', fontWeight: 700, cursor: 'pointer',
                            fontFamily: 'inherit',
                        }}
                    >
                        <FaTimes /> Fechar
                    </button>
                </div>
            </div>

            <style>{`
                @keyframes mr-spin { to { transform: rotate(360deg); } }
            `}</style>
        </div>
    );
}